import { Box, Button, Typography } from '@mui/material';
import { BarChart3, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';

import { useVisibilityStats } from '@modules/_shared/hooks/useVisibilityStats';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { IconBadge } from '@modules/_shared/shared/components/IconBadge';
import MetricItem from '@modules/_shared/shared/components/MetricItem';
import {
  VISIBILITY_METRICS_DEFINITIONS,
  getVisibilityMetricValue,
} from '@modules/_shared/utils/visibilityMetricsConfig';
import { useLocale } from '@shared/hooks/locale';
import {
  getMainRaTenantId,
  getMainRaTenantSwitchUrl,
  isRATenantId,
} from '@shared/utils/tenant';

import DashboardEmptyState from './shared/DashboardEmptyState';
import { DashboardSectionShell } from './shared/DashboardSectionShell';
import { useDashboardModules } from './shared/useDashboardModules';

export const DashboardStatsCard = () => {
  const { t, locale } = useLocale();
  const { me } = useAppData();
  const teamId = me?.default_team?.id;

  const { metrics, isLoading } = useVisibilityStats({ teamId });
  const { isVisibilityEnabled } = useDashboardModules();

  if (!isVisibilityEnabled) {
    const countryCode = me?.tenant?.country_code;
    // The "Open RealAdvisor" shortcut only makes sense for white-label
    // (non-RealAdvisor) tenants — RealAdvisor tenants are already in the main
    // environment.
    const isNonRaTenant = me?.tenant_id != null && !isRATenantId(me.tenant_id);
    // Token-free check for render; the switch URL itself reads the refresh
    // token and must be built at click time (not on render).
    const canSwitch = getMainRaTenantId(countryCode) != null;

    return (
      <DashboardSectionShell title={t('Visibility statistics')}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2.5,
          }}
        >
          <DashboardEmptyState
            message={
              isNonRaTenant
                ? t(
                    'See your visibility performance in the main RealAdvisor environment',
                  )
                : t('Visibility statistics are not enabled for your account')
            }
          />
          {isNonRaTenant && (
            <Button
              onClick={() => {
                const switchUrl = getMainRaTenantSwitchUrl(countryCode);
                if (switchUrl != null) {
                  window.open(switchUrl, '_blank', 'noopener,noreferrer');
                }
              }}
              disabled={!canSwitch}
              variant="soft"
              color="primary"
              fullWidth
              startIcon={<ExternalLink />}
            >
              {countryCode != null
                ? `${t('Open RealAdvisor')} (${countryCode})`
                : t('Open RealAdvisor')}
            </Button>
          )}
        </Box>
      </DashboardSectionShell>
    );
  }

  return (
    <DashboardSectionShell
      title={t('Visibility statistics')}
      action={
        <Typography
          variant="caption"
          sx={{ color: 'text.secondary', fontWeight: 500 }}
        >
          {t('Last 28 days')}
        </Typography>
      }
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2.5,
        }}
      >
        <Box
          sx={{
            display: 'grid',

            gridTemplateColumns: {
              xs: 'repeat(2, 1fr)',
              sm: 'repeat(4, 1fr)',
              lg: 'repeat(2, 1fr)',
            },

            rowGap: 2.5,
            columnGap: 2,
          }}
        >
          {VISIBILITY_METRICS_DEFINITIONS.map(definition => (
            <MetricItem
              key={definition.key}
              icon={
                <IconBadge
                  icon={definition.icon}
                  color="primary.main"
                  size="md"
                />
              }
              value={getVisibilityMetricValue(definition, metrics, locale)}
              label={definition.getLabel(t)}
              loading={isLoading}
              tooltip={definition.getTooltip?.(t)}
            />
          ))}
        </Box>

        <Button
          component={Link}
          to={
            teamId ? `/visibility/overview/${teamId}` : '/visibility/overview'
          }
          variant="soft"
          color="primary"
          fullWidth
          startIcon={<BarChart3 />}
        >
          {t('View more statistics')}
        </Button>
      </Box>
    </DashboardSectionShell>
  );
};
